import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X, Users, Image as ImageIcon, File, Bell, BellOff, Trash2, UserX, UserMinus, Star, Crown, Shield, UserPlus, Camera, Loader2, MessageCircle, Edit2, Check, X as XIcon, RotateCw, RotateCcw, ZoomIn } from 'lucide-react';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { groupApi, getApiBaseUrl, chatApi, avatarApi } from '../../services/cloudApi';
import AvatarCropModal from '../Common/AvatarCropModal';
import { showPlusGoldNotification } from '../../utils/plusGoldNotification';
import ImageModal from './ImageModal';
import DeleteModal from '../Common/DeleteModal';
import { getPlusEmailColor } from '../../utils/userDisplay';
import Username, { getDisplayName } from '../Common/Username';
import GroupAvatar from './GroupAvatar';
import ContextMenu from '../Common/ContextMenu';
import { getSocket } from '../../services/socketService';
import { useMembership } from '../../hooks/useMembership';
import { UPGRADE_MESSAGES } from '../../utils/membershipConstants';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

// Helper function to get user initials
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
};

const GroupInfoModal = ({ isOpen, onClose, group, asPage = false }) => {
  const { t, i18n } = useTranslation('chat');
  const { muteGroup, leaveGroup, deleteGroup, selectGroup, friends, reloadFriends, loadGroupMessages, updateGroup, selectFriend, loadGroups } = useChat();
  
  // ESLint fix: Ensure loadGroups is defined
  if (!loadGroups) {
    console.warn('loadGroups is not available from useChat hook');
  }
  const { authToken, user } = useAuth();
  const { limits } = useMembership();
  const maxUploadBytes = limits?.maxUploadBytes ?? 15 * 1024 * 1024;
  const [groupDetails, setGroupDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDestroyConfirm, setShowDestroyConfirm] = useState(false);
  const [showClearMessagesConfirm, setShowClearMessagesConfirm] = useState(false);
  const [showDeleteMessagesConfirm, setShowDeleteMessagesConfirm] = useState(false);
  const [showRemoveMemberConfirm, setShowRemoveMemberConfirm] = useState(null);
  const [mediaFiles, setMediaFiles] = useState({ images: [], documents: [] });
  const [activeTab, setActiveTab] = useState('info');
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [addMemberError, setAddMemberError] = useState(null);
  const [starredMessages, setStarredMessages] = useState([]);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showGroupCropModal, setShowGroupCropModal] = useState(false);
  const [selectedGroupImageFile, setSelectedGroupImageFile] = useState(null);
  const avatarInputRef = useRef(null);
  const [memberContextMenu, setMemberContextMenu] = useState({ isOpen: false, x: 0, y: 0, member: null });
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [updatingName, setUpdatingName] = useState(false);

  useEffect(() => {
    if (isOpen && group?.id && authToken) {
      loadGroupDetails();
      if (reloadFriends) {
        reloadFriends();
      }
      if (activeTab === 'starred') {
        loadStarredMessages();
      }
    }
  }, [isOpen, group?.id, authToken, reloadFriends, activeTab]);

  // CRITICAL FIX: Sync groupDetails with group prop from ChatContext
  // This ensures real-time updates when group is updated via socket events
  useEffect(() => {
    if (group && isOpen) {
      // Always sync groupDetails with the latest group prop
      // This ensures avatar/name changes from ChatContext are immediately reflected
      const normalizedGroup = {
        ...group,
        avatar_url: group.avatar_url || group.avatarUrl,
        avatarUrl: group.avatarUrl || group.avatar_url,
      };
      
      // Only update if there's a meaningful change to avoid unnecessary re-renders
      if (!groupDetails || 
          groupDetails.name !== normalizedGroup.name ||
          (groupDetails.avatar_url || groupDetails.avatarUrl) !== (normalizedGroup.avatar_url || normalizedGroup.avatarUrl)) {
        setGroupDetails(normalizedGroup);
      }
    }
  }, [group?.id, group?.name, group?.avatar_url, group?.avatarUrl, isOpen]);

  // CRITICAL FIX: Socket events are handled by ChatContext
  // The group prop will automatically update when ChatContext receives group:updated event
  // No need for duplicate socket listener here - rely on ChatContext as single source of truth

  const loadGroupDetails = async () => {
    if (!group?.id || !authToken) return;
    
    setIsLoading(true);
    try {
      const result = await groupApi.getGroup({ token: authToken, groupId: group.id });
      if (result?.ok && result?.group) {
        // Ensure avatar_url is properly set from both sources
        const groupData = {
          ...result.group,
          avatar_url: result.group.avatar_url || result.group.avatarUrl || group.avatar_url || group.avatarUrl,
          name: result.group.name || group.name,
        };
        setGroupDetails(groupData);
      } else {
        // Fallback to group prop if API call fails
        setGroupDetails({
          ...group,
          avatar_url: group.avatar_url || group.avatarUrl,
          name: group.name,
        });
      }
      await loadMediaFiles();
    } catch (error) {
      console.error('Load group details error:', error);
      // Fallback to group prop on error, but merge with existing groupDetails if available
      setGroupDetails(prev => ({
        ...prev,
        ...group,
        avatar_url: group.avatar_url || group.avatarUrl || prev?.avatar_url,
        name: group.name || prev?.name,
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('groupInfoModal.errors.selectImageTitle'), message: t('groupInfoModal.errors.selectImageMessage') },
      }));
      return;
    }

    if (file.size > maxUploadBytes) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('groupInfoModal.errors.fileSizeLimitTitle'), message: UPGRADE_MESSAGES.uploadLimit },
      }));
      return;
    }

    setSelectedGroupImageFile(file);
    setShowGroupCropModal(true);
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  const handleGroupAvatarSave = async (croppedFile) => {
    if (!authToken || !group?.id) return;
    setUploadingAvatar(true);
    try {
      const result = await avatarApi.uploadGroup({ token: authToken, groupId: group.id, file: croppedFile });
      if (!result?.ok) throw new Error(result?.message || t('groupInfoModal.uploadFailedFallback'));

      const finalAvatarUrl = result.avatarUrl;
      setGroupDetails((prev) => ({
        ...(prev || group),
        avatar_url: finalAvatarUrl,
        avatarUrl: finalAvatarUrl,
      }));

      if (typeof loadGroups === 'function') {
        loadGroups(true);
      }
      await loadGroupDetails();

      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'success', title: t('groupInfoModal.avatarUpdatedTitle'), message: t('groupInfoModal.avatarUpdatedMessage') },
      }));
    } catch (error) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('groupInfoModal.avatarUpdateErrorTitle'), message: getApiErrorMessage(error, { fallback: t('groupInfoModal.avatarUpdateErrorFallback') }) },
      }));
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Handle group name update
  const handleNameUpdate = async () => {
    if (!editingName || !editingName.trim() || !authToken || !group?.id) return;
    
    const trimmedName = editingName.trim();
    const currentName = groupDetails?.name || group.name;
    
    // If name hasn't changed, just cancel editing
    if (trimmedName === currentName) {
      setIsEditingName(false);
      setEditingName('');
      return;
    }

    setUpdatingName(true);
    try {
      const updatedGroup = await updateGroup(group.id, {
        name: trimmedName,
      });

      if (updatedGroup) {
        // Update groupDetails with the latest data
        const updatedGroupData = {
          ...updatedGroup,
          avatar_url: updatedGroup.avatar_url || updatedGroup.avatarUrl || groupDetails?.avatar_url,
        };
        setGroupDetails(updatedGroupData);
        setIsEditingName(false);
        setEditingName('');
        
        // Reload group details to ensure we have the latest data including members
        await loadGroupDetails();
        
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'success',
            title: t('groupInfoModal.nameUpdatedTitle'),
            message: t('groupInfoModal.nameUpdatedMessage'),
          },
        }));
      }
    } catch (error) {
      console.error('Name update error:', error);
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: {
          type: 'error',
          title: t('groupInfoModal.nameUpdateErrorTitle'),
          message: getApiErrorMessage(error, { fallback: t('groupInfoModal.nameUpdateErrorFallback') }),
        },
      }));
    } finally {
      setUpdatingName(false);
    }
  };

  // Start editing name
  const startEditingName = () => {
    const currentName = groupDetails?.name || group.name;
    setEditingName(currentName);
    setIsEditingName(true);
  };

  // Cancel editing name
  const cancelEditingName = () => {
    setIsEditingName(false);
    setEditingName('');
  };

  const loadMediaFiles = async () => {
    if (!group?.id || !authToken) return;
    
    try {
      const result = await groupApi.getGroupMessages({ token: authToken, groupId: group.id });
      if (result?.ok && result?.messages) {
        const apiBaseUrl = await getApiBaseUrl();
        const baseUrl = apiBaseUrl.replace('/api', '');
        
        const images = result.messages.filter((msg) => 
          msg.type === 'image' && msg.file_url
        ).map((msg) => {
          let fileUrl = msg.file_url;
          if (fileUrl && !fileUrl.startsWith('http')) {
            fileUrl = `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
          }
          return {
            id: msg.id,
            url: fileUrl,
            createdAt: msg.created_at || msg.timestamp,
          };
        });

        const documents = result.messages.filter((msg) => 
          msg.type === 'file' && msg.file_url
        ).map((msg) => {
          let fileUrl = msg.file_url;
          if (fileUrl && !fileUrl.startsWith('http')) {
            fileUrl = `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
          }
          return {
            id: msg.id,
            url: fileUrl,
            fileName: msg.file_name || msg.content || t('groupInfoModal.fileDefaultName'),
            createdAt: msg.created_at || msg.timestamp,
          };
        });

        setMediaFiles({ images, documents });
      }
    } catch (error) {
      console.error('Load media files error:', error);
    }
  };

  const loadStarredMessages = async () => {
    if (!group?.id || !authToken) return;
    
    try {
      // Use dedicated starred messages endpoint
      const result = await groupApi.getStarredGroupMessages({ token: authToken, groupId: group.id });
      if (result?.ok && result?.messages) {
        setStarredMessages(result.messages);
      } else {
        setStarredMessages([]);
      }
    } catch (error) {
      console.error('Load starred messages error:', error);
      setStarredMessages([]);
    }
  };

  const handleLeaveGroup = async () => {
    if (!group?.id) return;
    await leaveGroup(group.id);
    setShowLeaveConfirm(false);
    onClose();
    selectGroup(null);
  };

  const handleDeleteGroup = async () => {
    if (!group?.id) return;
    await deleteGroup(group.id);
    setShowDeleteConfirm(false);
    onClose();
    selectGroup(null);
  };

  const handleDestroyGroup = async () => {
    if (!group?.id || !authToken) return;
    
    try {
      const result = await groupApi.destroyGroup({
        token: authToken,
        groupId: group.id,
      });
      
      if (result?.ok) {
        setShowDestroyConfirm(false);
        await loadGroupDetails();
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'success',
              title: t('groupInfoModal.destroyedTitle'),
              message: t('groupInfoModal.destroyedMessage'),
            },
          })
        );
      }
    } catch (error) {
      console.error('Destroy group error:', error);
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('groupInfoModal.destroyErrorTitle'),
            message: t('groupInfoModal.destroyErrorMessage'),
          },
        })
      );
    }
  };

  const handleClearMessages = async () => {
    if (!group?.id || !authToken) return;
    
    try {
      const result = await groupApi.clearGroupMessages({
        token: authToken,
        groupId: group.id,
      });
      
      if (result?.ok) {
        setShowClearMessagesConfirm(false);
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'success',
              title: t('groupInfoModal.messagesClearedTitle'),
              message: t('groupInfoModal.messagesClearedMessage'),
            },
          })
        );
        // Reload group messages
        if (loadGroupMessages && group?.id) {
          await loadGroupMessages(group.id);
        } else if (selectGroup) {
          window.dispatchEvent(new CustomEvent('notesplus-reload-group-messages', { detail: { groupId: group.id } }));
        }
      }
    } catch (error) {
      console.error('Clear messages error:', error);
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('groupInfoModal.clearErrorTitle'),
            message: t('groupInfoModal.clearErrorMessage'),
          },
        })
      );
    }
  };

  const handleDeleteMessages = async () => {
    if (!group?.id || !authToken) return;
    
    try {
      const result = await groupApi.deleteGroupMessages({
        token: authToken,
        groupId: group.id,
      });
      
      if (result?.ok) {
        setShowDeleteMessagesConfirm(false);
        if (selectGroup) {
          selectGroup(null);
        }
        onClose();
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'success',
              title: t('groupInfoModal.messagesDeletedTitle'),
              message: t('groupInfoModal.messagesDeletedMessage'),
            },
          })
        );
      }
    } catch (error) {
      console.error('Delete messages error:', error);
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('groupInfoModal.deleteErrorTitle'),
            message: t('groupInfoModal.deleteErrorMessage'),
          },
        })
      );
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!group?.id || !memberId || !authToken) return;
    
    if (isLocked) {
      setShowRemoveMemberConfirm(null);
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('groupInfoModal.groupLockedTitle'),
            message: t('groupInfoModal.groupLockedRemoveMemberMessage'),
          },
        })
      );
      return;
    }

    try {
      const result = await groupApi.removeMember({
        token: authToken,
        groupId: group.id,
        memberId,
      });

      if (result?.ok) {
        await loadGroupDetails();
        setShowRemoveMemberConfirm(null);
      } else {
        if (result?.error === 'GROUP_LOCKED') {
          window.dispatchEvent(
            new CustomEvent('notesplus-show-toast', {
              detail: {
                type: 'error',
                title: t('groupInfoModal.groupLockedTitle'),
                message: result.message || t('groupInfoModal.groupLockedRemoveMemberMessage'),
              },
            })
          );
        } else {
          window.dispatchEvent(
            new CustomEvent('notesplus-show-toast', {
              detail: {
                type: 'error',
                title: t('groupInfoModal.removeMemberErrorTitle'),
                message: result?.message || t('groupInfoModal.removeMemberErrorFallback'),
              },
            })
          );
        }
        setShowRemoveMemberConfirm(null);
      }
    } catch (error) {
      console.error('Remove member error:', error);
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('groupInfoModal.removeMemberErrorTitle'),
            message: getApiErrorMessage(error, { fallback: t('groupInfoModal.removeMemberErrorFallback') }),
          },
        })
      );
      setShowRemoveMemberConfirm(null);
    }
  };

  const handleToggleMute = async () => {
    if (!group?.id) return;
    await muteGroup(group.id, !group.muted);
    await loadGroupDetails();
  };

  const handleAddMember = async (friendEmail) => {
    if (!group?.id || !authToken || !friendEmail) return;

    if (isLocked) {
      setAddMemberError(t('groupInfoModal.groupLockedAddMemberMessage'));
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('groupInfoModal.groupLockedTitle'),
            message: t('groupInfoModal.groupLockedAddMemberMessage'),
          },
        })
      );
      return;
    }

    setAddingMember(true);
    setAddMemberError(null);

    try {
      const result = await groupApi.addMember({
        token: authToken,
        groupId: group.id,
        memberEmail: friendEmail,
      });

      if (result?.ok) {
        setShowAddMemberModal(false);
        await loadGroupDetails();
        
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'success',
              title: t('groupInfoModal.memberAddedTitle'),
              message: t('groupInfoModal.memberAddedMessage', { email: friendEmail }),
            },
          })
        );
      } else {
        setAddMemberError(result?.message || t('groupInfoModal.addMemberErrorFallback'));

        if (result?.error === 'GROUP_LOCKED') {
          window.dispatchEvent(
            new CustomEvent('notesplus-show-toast', {
              detail: {
                type: 'error',
                title: t('groupInfoModal.groupLockedTitle'),
                message: result.message || t('groupInfoModal.groupLockedAddMemberMessage'),
              },
            })
          );
        }

        if (result?.error === 'GROUP_LIMIT_REACHED') {
          showPlusGoldNotification(t('groupInfoModal.groupLimitReachedLabel'), result.message || t('groupInfoModal.groupLimitReachedMessage'));
        }
      }
    } catch (error) {
      console.error('Add member error:', error);
      setAddMemberError(getApiErrorMessage(error, { fallback: t('groupInfoModal.addMemberErrorFallback') }));
    } finally {
      setAddingMember(false);
    }
  };

  if (!isOpen || !group) return null;

  // CRITICAL: Use groupDetails if available, otherwise fallback to group prop
  // Ensure avatar_url is always normalized for GroupAvatar component
  const currentGroup = groupDetails || group;
  const currentGroupWithAvatar = currentGroup ? {
    ...currentGroup,
    avatar_url: currentGroup.avatar_url || currentGroup.avatarUrl || null,
    avatarUrl: currentGroup.avatarUrl || currentGroup.avatar_url || null,
  } : null;
  const currentUserRole = groupDetails?.userRole || group?.role;
  const isOwner = currentUserRole === 'owner';
  const isAdmin = currentUserRole === 'admin';
  const canManage = isOwner || isAdmin;
  const isDestroyed = groupDetails?.is_active === false || group?.is_active === false;
  const isLocked = groupDetails?.isLocked === true || group?.isLocked === true;

  const renderContent = () => (
    <>
      {/* Tabs */}
      <div className="flex border-b border-white/10 dark:border-white/10 bg-[var(--color-bg-muted)] dark:bg-[var(--color-bg-elevated)]/50">
        <button
          onClick={() => setActiveTab('info')}
          className={`px-6 py-3 text-sm font-medium transition-colors relative ${
            activeTab === 'info'
              ? 'text-[#3d8ef0] dark:text-[#7ab8f8]'
              : 'text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] dark:hover:text-white'
          }`}
        >
          {t('groupInfoModal.tabs.info')}
          {activeTab === 'info' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#2563eb] dark:bg-primary-400" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('media')}
          className={`px-6 py-3 text-sm font-medium transition-colors relative ${
            activeTab === 'media'
              ? 'text-[#3d8ef0] dark:text-[#7ab8f8]'
              : 'text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] dark:hover:text-white'
          }`}
        >
          {t('groupInfoModal.tabs.media')}
          {activeTab === 'media' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#2563eb] dark:bg-primary-400" />
          )}
        </button>
        <button
          onClick={() => {
            setActiveTab('starred');
            loadStarredMessages();
          }}
          className={`px-6 py-3 text-sm font-medium transition-colors relative ${
            activeTab === 'starred'
              ? 'text-[#3d8ef0] dark:text-[#7ab8f8]'
              : 'text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] dark:hover:text-white'
          }`}
        >
          {t('groupInfoModal.tabs.starred')}
          {activeTab === 'starred' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#2563eb] dark:bg-primary-400" />
          )}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'info' ? (
          <div className="p-6">
            {/* Two Column Layout */}
            <div className="grid grid-cols-[280px_1fr] gap-6">
              {/* Left Column - Group Info */}
              <div className="space-y-4">
                {/* Group Header */}
                <div className="flex flex-col items-center text-center">
                  <div className="relative mb-3 group">
                    <div
                      className={`relative ${currentGroupWithAvatar?.avatar_url ? 'cursor-pointer' : ''}`}
                      onClick={() => {
                        const url = currentGroupWithAvatar?.avatar_url || currentGroupWithAvatar?.avatarUrl;
                        if (url) {
                          setSelectedImageUrl(url.startsWith('http') ? url : `${getApiBaseUrl().replace('/api', '')}${url}`);
                          setShowImageModal(true);
                        }
                      }}
                    >
                      <GroupAvatar
                        group={groupDetails || group}
                        size="xl"
                        className="shadow-lg"
                      />
                      {(currentGroupWithAvatar?.avatar_url || currentGroupWithAvatar?.avatarUrl) && (
                        <div className="absolute inset-0 rounded-full bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                          <ZoomIn className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>
                    {group.is_plus_group && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center border-2 border-white/10 shadow-elevated">
                        <Star className="w-3.5 h-3.5 text-white fill-white" />
                      </div>
                    )}
                    {canManage && (
                      <>
                        <input
                          ref={avatarInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarSelect}
                          className="hidden"
                        />
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => avatarInputRef.current?.click()}
                          disabled={uploadingAvatar}
                          className="absolute bottom-0 right-0 w-8 h-8 bg-[#2563eb] hover:bg-[#3d8ef0] dark:bg-[#2563eb] dark:hover:bg-[#2563eb] rounded-full flex items-center justify-center text-white shadow-lg border-2 border-white dark:border-white/[0.07] transition-all opacity-100 group-hover:opacity-100"
                          title={t('groupInfoModal.changePhotoTitle')}
                        >
                          {uploadingAvatar ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Camera className="w-4 h-4" />
                          )}
                        </motion.button>
                      </>
                    )}
                  </div>
                  <div className="w-full">
                    {isEditingName ? (
                      <div className="flex items-center gap-2 mb-1">
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleNameUpdate();
                            } else if (e.key === 'Escape') {
                              cancelEditingName();
                            }
                          }}
                          disabled={updatingName}
                          className="flex-1 px-2 py-1 text-lg font-bold text-[var(--color-text-primary)] dark:text-white bg-[var(--color-bg-elevated)] dark:bg-[#161f31] rounded-lg border border-[#3d8ef0]/50 focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                          autoFocus
                        />
                        <button
                          onClick={handleNameUpdate}
                          disabled={updatingName || !editingName.trim()}
                          className="p-1.5 rounded-lg bg-[#2563eb] hover:bg-[#3d8ef0] dark:bg-[#2563eb] dark:hover:bg-[#2563eb] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={t('groupInfoModal.saveTitle')}
                        >
                          {updatingName ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={cancelEditingName}
                          disabled={updatingName}
                          className="p-1.5 rounded-lg bg-[#24354f] dark:bg-[#161f31] hover:bg-gray-300 dark:hover:bg-[#24354f] text-[var(--color-text-secondary)] dark:text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={t('groupInfoModal.cancelTitle')}
                        >
                          <XIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 justify-center mb-1">
                        <h3 className="text-lg font-bold text-[var(--color-text-primary)] dark:text-white">
                          {groupDetails?.name || group.name}
                        </h3>
                        {canManage && (
                          <button
                            onClick={startEditingName}
                            className="p-1 rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] dark:hover:text-white transition-colors"
                            title={t('groupInfoModal.editNameTitle')}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                    {(groupDetails?.description || group.description) && (
                      <p className="text-xs text-[var(--color-text-secondary)] dark:text-[var(--color-text-muted)]">
                        {groupDetails?.description || group.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Quick Actions - Compact Grid */}
                <div className="space-y-2">
                  <button
                    onClick={handleToggleMute}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-bg-muted)] dark:bg-[#161f31]/30 hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] transition-colors"
                  >
                    {group.muted ? (
                      <BellOff className="w-4 h-4 text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]" />
                    ) : (
                      <Bell className="w-4 h-4 text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]" />
                    )}
                    <span className="text-sm text-[var(--color-text-secondary)] dark:text-gray-300">
                      {group.muted ? t('groupInfoModal.muted') : t('groupInfoModal.unmuted')}
                    </span>
                  </button>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowClearMessagesConfirm(true)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-[var(--color-bg-elevated)] dark:bg-[#161f31] hover:bg-[#24354f] dark:hover:bg-[#24354f] text-[var(--color-text-secondary)] dark:text-gray-300 rounded-lg transition-colors text-xs font-medium"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {t('groupInfoModal.clear')}
                    </button>

                    {isOwner ? (
                      !isDestroyed ? (
                        <button
                          onClick={() => setShowDestroyConfirm(true)}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors text-xs font-medium"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          {t('groupInfoModal.destroy')}
                        </button>
                      ) : (
                        <button
                          onClick={() => setShowDeleteMessagesConfirm(true)}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors text-xs font-medium"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          {t('groupInfoModal.delete')}
                        </button>
                      )
                    ) : (
                      <button
                        onClick={() => setShowLeaveConfirm(true)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors text-xs font-medium"
                      >
                        <UserX className="w-3.5 h-3.5" />
                        {t('groupInfoModal.leave')}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Members List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-[var(--color-text-secondary)] dark:text-gray-300">
                    {t('groupInfoModal.membersLabel', { count: groupDetails?.members?.length || 0 })}
                  </h4>
                  {canManage && (
                    <button
                      onClick={() => {
                        if (isLocked) {
                          window.dispatchEvent(
                            new CustomEvent('notesplus-show-toast', {
                              detail: {
                                type: 'error',
                                title: t('groupInfoModal.groupLockedTitle'),
                                message: t('groupInfoModal.groupLockedAddMemberMessage'),
                              },
                            })
                          );
                          return;
                        }
                        if (reloadFriends) {
                          reloadFriends();
                        }
                        setShowAddMemberModal(true);
                      }}
                      disabled={isLocked}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        isLocked
                          ? 'bg-[#24354f] dark:bg-[#161f31] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] cursor-not-allowed'
                          : 'bg-primary-50 dark:bg-[#2563eb]/10/20 text-[#3d8ef0] dark:text-[#7ab8f8] hover:bg-[#2563eb]/10 dark:hover:bg-[#2563eb]/10'
                      }`}
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      {t('groupInfoModal.addMember')}
                    </button>
                  )}
                </div>
                <div className="space-y-1.5 max-h-[calc(90vh-300px)] overflow-y-auto">
                  {groupDetails?.members?.map((member) => {
                    const isCurrentUser = member.id === user?.id;
                    const canRemove = canManage && !isCurrentUser && member.role !== 'owner' && !isLocked;

                    let roleLabel = t('groupInfoModal.roleMember');
                    if (member.role === 'owner') roleLabel = t('groupInfoModal.roleOwner');
                    else if (member.role === 'admin') roleLabel = t('groupInfoModal.roleAdmin');
                    
                    return (
                      <div
                        key={member.id}
                        className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-[var(--color-bg-muted)] dark:hover:bg-[#1e2d47]/30 transition-colors"
                        onContextMenu={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setMemberContextMenu({
                            isOpen: true,
                            x: e.clientX,
                            y: e.clientY,
                            member,
                          });
                        }}
                      >
                        <div className="w-9 h-9 rounded-full bg-[#2563eb]/10 dark:bg-[#2563eb]/10 flex items-center justify-center text-xs font-semibold text-[#3d8ef0] dark:text-[#7ab8f8] flex-shrink-0">
                          {member.avatarUrl ? (
                            <img
                              src={member.avatarUrl}
                              alt={getDisplayName(member)}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            getInitials(getDisplayName(member))
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-medium text-[var(--color-text-primary)] dark:text-white truncate">
                              {getDisplayName(member)}
                            </p>
                            {isCurrentUser && (
                              <span className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]">{t('groupInfoModal.youTag')}</span>
                            )}
                            {member.role === 'owner' && (
                              <Crown className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />
                            )}
                            {member.role === 'admin' && (
                              <Shield className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                            )}
                          </div>
                          {member.email && (
                            <p className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] truncate">
                              {member.email}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]">
                              {roleLabel}
                            </span>
                            {isCurrentUser && (
                              <span className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]">{t('groupInfoModal.youTag')}</span>
                            )}
                          </div>
                        </div>
                        
                        {canRemove && (
                          <button
                            onClick={() => setShowRemoveMemberConfirm(member)}
                            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-colors"
                            title={t('groupInfoModal.removeMemberTitle')}
                          >
                            <UserMinus className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'media' ? (
          <div className="p-6 space-y-6">
            {/* Images Section */}
            <div>
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] dark:text-white mb-4 flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                {t('groupInfoModal.photosLabel', { count: mediaFiles.images.length })}
              </h3>
              {mediaFiles.images.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {mediaFiles.images.map((image) => (
                    <motion.div
                      key={image.id}
                      whileHover={{ scale: 1.05 }}
                      className="aspect-square rounded-lg overflow-hidden cursor-pointer bg-[var(--color-bg-elevated)] dark:bg-[#161f31]"
                      onClick={() => {
                        setSelectedImageUrl(image.url);
                        setShowImageModal(true);
                      }}
                    >
                      <img
                        src={image.url}
                        alt={t('groupInfoModal.groupImageAlt')}
                        className="w-full h-full object-cover"
                      />
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] text-center py-8">
                  {t('groupInfoModal.noPhotosYet')}
                </p>
              )}
            </div>

            {/* Documents Section */}
            <div>
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] dark:text-white mb-4 flex items-center gap-2">
                <File className="w-5 h-5" />
                {t('groupInfoModal.documentsLabel', { count: mediaFiles.documents.length })}
              </h3>
              {mediaFiles.documents.length > 0 ? (
                <div className="space-y-2">
                  {mediaFiles.documents.map((doc) => (
                    <motion.div
                      key={doc.id}
                      whileHover={{ scale: 1.02 }}
                      className="flex items-center gap-3 p-3 rounded-lg bg-[var(--color-bg-muted)] dark:bg-[#161f31] cursor-pointer hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47]"
                      onClick={() => window.open(doc.url, '_blank')}
                    >
                      <File className="w-8 h-8 text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--color-text-primary)] dark:text-white truncate">
                          {doc.fileName}
                        </p>
                        <p className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]">
                          {new Date(doc.createdAt).toLocaleDateString(i18n.language)}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] text-center py-8">
                  {t('groupInfoModal.noDocumentsYet')}
                </p>
              )}
            </div>
          </div>
        ) : activeTab === 'starred' ? (
          <div className="p-6">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] dark:text-white mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              {t('groupInfoModal.starredLabel', { count: starredMessages.length })}
            </h3>
            {starredMessages.length > 0 ? (
              <div className="space-y-2">
                {starredMessages.map((message) => (
                  <div
                    key={message.id}
                    className="p-3 rounded-lg bg-[var(--color-bg-muted)] dark:bg-[#161f31] hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] transition-colors cursor-pointer"
                    onClick={() => {
                      // Scroll to message in chat view
                      window.dispatchEvent(new CustomEvent('notesplus-scroll-to-message', { 
                        detail: { groupId: group.id, messageId: message.id } 
                      }));
                      onClose();
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[var(--color-text-primary)] dark:text-white line-clamp-2">
                          {message.type === 'text'
                            ? message.content?.replace(/<[^>]*>/g, '').substring(0, 100)
                            : message.file_name || t('groupInfoModal.fileDefaultName')}
                        </p>
                        <p className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] mt-1">
                          {new Date(message.created_at || message.timestamp).toLocaleString(i18n.language)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Star className="w-12 h-12 text-[var(--color-text-secondary)] dark:text-[var(--color-text-muted)] mx-auto mb-3" />
                <p className="text-sm text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]">
                  {t('groupInfoModal.noStarredYet')}
                </p>
                <p className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] mt-1">
                  {t('groupInfoModal.starredHint')}
                </p>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </>
  );

  if (asPage) {
    return (
      <>
      <motion.div
        initial={{ opacity: 0, x: '100%' }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: '100%' }}
        transition={{ duration: 0.2 }}
        className="h-full w-full flex flex-col bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] overflow-hidden"
      >
        <div className="flex items-center gap-3 p-4 border-b border-white/10 dark:border-white/10 bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)]">
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] dark:hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] dark:text-white">
            {t('groupInfoModal.groupInfoTitle')}
          </h2>
      </div>

      {/* Member Context Menu (participant right-click) */}
      <ContextMenu
        isOpen={memberContextMenu.isOpen && !!memberContextMenu.member}
        position={{ x: memberContextMenu.x, y: memberContextMenu.y }}
        items={(() => {
          const member = memberContextMenu.member;
          if (!member) return [];

          const isCurrentUser = member.id === user?.id;
          const isFriend = friends?.some(
            (f) => !f.isDeleted && (f.email || f.id) === member.email
          );

          const isLocked =
            currentGroup?.isLocked === true || currentGroup?.is_locked === true;

          const canKickMember =
            !isCurrentUser &&
            !isLocked &&
            member.role !== 'owner' &&
            ((currentUserRole === 'owner') ||
              (currentUserRole === 'admin' && member.role === 'member'));

          const canPromoteToAdmin =
            currentUserRole === 'owner' &&
            member.role === 'member' &&
            !isCurrentUser;

          const canDemoteToMember =
            currentUserRole === 'owner' &&
            member.role === 'admin' &&
            !isCurrentUser;

          return [
            // Özel mesaj gönder
            !isCurrentUser &&
              isFriend && {
                label: t('groupInfoModal.contextMenu.sendPrivateMessage'),
                icon: MessageCircle,
                onClick: () => {
                  const friendId = member.email || member.id;
                  if (friendId && selectFriend) {
                    selectFriend(friendId);
                  }
                },
              },
            // Arkadaş ekle
            !isCurrentUser &&
              !isFriend &&
              member.email && {
                label: t('groupInfoModal.contextMenu.addFriend'),
                icon: UserPlus,
                onClick: async () => {
                  try {
                    await chatApi.sendFriendRequest({
                      token: authToken,
                      receiverEmail: member.email,
                    });
                    window.dispatchEvent(
                      new CustomEvent('notesplus-show-toast', {
                        detail: {
                          type: 'success',
                          title: t('groupInfoModal.contextMenu.friendRequestTitle'),
                          message: t('groupInfoModal.contextMenu.friendRequestMessage', { email: member.email }),
                        },
                      })
                    );
                  } catch (err) {
                    console.error(
                      'Send friend request error from member context menu (modal):',
                      err
                    );
                    const message = getApiErrorMessage(err, { fallback: t('groupInfoModal.contextMenu.friendRequestErrorFallback') });
                    window.dispatchEvent(
                      new CustomEvent('notesplus-show-toast', {
                        detail: {
                          type: 'error',
                          title: t('groupInfoModal.contextMenu.friendRequestErrorTitle'),
                          message,
                        },
                      })
                    );
                  }
                },
              },
            // Yönetici yap (sadece Kurucu, hedef UYE)
            canPromoteToAdmin && {
              label: t('groupInfoModal.contextMenu.makeAdmin'),
              icon: Shield,
              onClick: async () => {
                try {
                  await groupApi.promoteMember({
                    token: authToken,
                    groupId: currentGroup.id,
                    memberId: member.id,
                  });
                  await loadGroupDetails();
                } catch (err) {
                  console.error('Promote member error (modal):', err);
                  const message = getApiErrorMessage(err, { fallback: t('groupInfoModal.contextMenu.makeAdminErrorFallback') });
                  window.dispatchEvent(
                    new CustomEvent('notesplus-show-toast', {
                      detail: {
                        type: 'error',
                        title: t('common:errorTitle'),
                        message,
                      },
                    })
                  );
                }
              },
            },
            // Üye yap (sadece Kurucu, hedef YONETICI)
            canDemoteToMember && {
              label: t('groupInfoModal.contextMenu.makeMember'),
              icon: Users,
              onClick: async () => {
                try {
                  await groupApi.demoteMember({
                    token: authToken,
                    groupId: currentGroup.id,
                    memberId: member.id,
                  });
                  await loadGroupDetails();
                } catch (err) {
                  console.error('Demote member error (modal):', err);
                  const message = getApiErrorMessage(err, { fallback: t('groupInfoModal.contextMenu.makeMemberErrorFallback') });
                  window.dispatchEvent(
                    new CustomEvent('notesplus-show-toast', {
                      detail: {
                        type: 'error',
                        title: t('common:errorTitle'),
                        message,
                      },
                    })
                  );
                }
              },
            },
            // Gruptan at
            canKickMember && {
              label: t('groupInfoModal.contextMenu.kick'),
              icon: UserMinus,
              danger: true,
              onClick: () => {
                setShowRemoveMemberConfirm(member);
              },
            },
          ];
        })()}
        onClose={() =>
          setMemberContextMenu({ isOpen: false, x: 0, y: 0, member: null })
        }
      />
        <div className="flex-1 overflow-hidden flex flex-col">
          {renderContent()}
        </div>
        
        <ImageModal
          isOpen={showImageModal}
          onClose={() => {
            setShowImageModal(false);
            setSelectedImageUrl(null);
          }}
          imageUrl={selectedImageUrl}
        />

        <DeleteModal
          isOpen={showLeaveConfirm}
          onClose={() => setShowLeaveConfirm(false)}
          onConfirm={handleLeaveGroup}
          title={t('groupInfoModal.modals.leaveTitle')}
          message={t('groupInfoModal.modals.leaveMessage', { name: group.name })}
        />

        <DeleteModal
          isOpen={showDestroyConfirm}
          onClose={() => setShowDestroyConfirm(false)}
          onConfirm={handleDestroyGroup}
          title={t('groupInfoModal.modals.destroyTitle')}
          message={t('groupInfoModal.modals.destroyMessage', { name: group.name })}
        />

        <DeleteModal
          isOpen={showClearMessagesConfirm}
          onClose={() => setShowClearMessagesConfirm(false)}
          onConfirm={handleClearMessages}
          title={t('groupInfoModal.modals.clearTitle')}
          message={t('groupInfoModal.modals.clearMessage', { name: group.name })}
        />

        <DeleteModal
          isOpen={showDeleteMessagesConfirm}
          onClose={() => setShowDeleteMessagesConfirm(false)}
          onConfirm={handleDeleteMessages}
          title={t('groupInfoModal.modals.deleteMessagesTitle')}
          message={t('groupInfoModal.modals.deleteMessagesMessage', { name: group.name })}
        />

        <DeleteModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteGroup}
          title={t('groupInfoModal.modals.deleteGroupTitle')}
          message={t('groupInfoModal.modals.deleteGroupMessage', { name: group.name })}
        />

        {showRemoveMemberConfirm && (
          <DeleteModal
            isOpen={!!showRemoveMemberConfirm}
            onClose={() => setShowRemoveMemberConfirm(null)}
            onConfirm={() => handleRemoveMember(showRemoveMemberConfirm.id)}
            title={t('groupInfoModal.removeMemberTitle')}
            message={t('groupInfoModal.modals.removeMemberMessage', { name: getDisplayName(showRemoveMemberConfirm) })}
          />
        )}

        {/* Add Member Modal */}
        <AnimatePresence>
          {showAddMemberModal && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => {
                  setShowAddMemberModal(false);
                  setAddMemberError(null);
                }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100002]"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="fixed inset-0 flex items-center justify-center z-[100003] p-4 pointer-events-none"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col pointer-events-auto">
                  <div className="flex items-center justify-between p-4 border-b border-white/10 dark:border-white/10">
                    <h3 className="text-lg font-semibold text-[var(--color-text-primary)] dark:text-white">
                      {t('groupInfoModal.selectFriendTitle')}
                    </h3>
                    <button
                      onClick={() => {
                        setShowAddMemberModal(false);
                        setAddMemberError(null);
                      }}
                      className="p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4">
                    {(() => {
                      const availableFriends = friends?.filter(friend => {
                        if (friend.isDeleted) return false;
                        const friendId = friend.email || friend.id;
                        return !groupDetails?.members?.some(m => (m.email || m.id) === friendId);
                      }) || [];
                      
                      if (!friends || friends.length === 0) {
                        return (
                          <div className="text-center text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] py-8">
                            <p className="text-sm">{t('groupInfoModal.noFriendsYet')}</p>
                            <p className="text-xs mt-1">{t('groupInfoModal.addFriendFirst')}</p>
                          </div>
                        );
                      }

                      if (availableFriends.length === 0) {
                        return (
                          <div className="text-center text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] py-8">
                            <p className="text-sm">{t('groupInfoModal.allFriendsInGroup')}</p>
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-2">
                          {availableFriends.map((friend) => {
                            const friendId = friend.email || friend.id;
                            return (
                              <button
                                key={friendId}
                                onClick={() => handleAddMember(friend.email || friendId)}
                                disabled={addingMember}
                                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-[var(--color-bg-muted)] dark:hover:bg-[#1e2d47] transition-colors text-left"
                              >
                                <div className="w-10 h-10 rounded-full bg-[#2563eb]/10 dark:bg-[#2563eb]/10 flex items-center justify-center text-sm font-semibold text-[#3d8ef0] dark:text-[#7ab8f8] flex-shrink-0">
                                  {friend.avatarUrl ? (
                                    <img
                                      src={friend.avatarUrl}
                                      alt={getDisplayName(friend)}
                                      className="w-full h-full rounded-full object-cover"
                                    />
                                  ) : (
                                    getInitials(getDisplayName(friend))
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-[var(--color-text-primary)] dark:text-white truncate">
                                    {getDisplayName(friend)}
                                  </p>
                                  {friend.email && (
                                    <p className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] truncate">
                                      {friend.email}
                                    </p>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>

                  {addMemberError && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-red-500 dark:text-red-400">{addMemberError}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </motion.div>

      <AvatarCropModal
        isOpen={showGroupCropModal}
        onClose={() => setShowGroupCropModal(false)}
        imageFile={selectedGroupImageFile}
        onSave={handleGroupAvatarSave}
        title={t('groupInfoModal.avatarCropTitle')}
      />
      </>
    );
  }

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100000]"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[100001] flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-white/10 dark:border-white/10">
                <h2 className="text-xl font-bold text-[var(--color-text-primary)] dark:text-white">
                  {t('groupInfoModal.groupInfoTitle')}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] dark:hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-hidden flex flex-col">
                {renderContent()}
              </div>
            </div>
            
            <ImageModal
              isOpen={showImageModal}
              onClose={() => {
                setShowImageModal(false);
                setSelectedImageUrl(null);
              }}
              imageUrl={selectedImageUrl}
            />

            <DeleteModal
              isOpen={showLeaveConfirm}
              onClose={() => setShowLeaveConfirm(false)}
              onConfirm={handleLeaveGroup}
              title={t('groupInfoModal.modals.leaveTitle')}
              message={t('groupInfoModal.modals.leaveMessage', { name: group.name })}
            />

            <DeleteModal
              isOpen={showDestroyConfirm}
              onClose={() => setShowDestroyConfirm(false)}
              onConfirm={handleDestroyGroup}
              title={t('groupInfoModal.modals.destroyTitle')}
              message={t('groupInfoModal.modals.destroyMessage', { name: group.name })}
            />

            <DeleteModal
              isOpen={showClearMessagesConfirm}
              onClose={() => setShowClearMessagesConfirm(false)}
              onConfirm={handleClearMessages}
              title={t('groupInfoModal.modals.clearTitle')}
              message={t('groupInfoModal.modals.clearMessage', { name: group.name })}
            />

            <DeleteModal
              isOpen={showDeleteMessagesConfirm}
              onClose={() => setShowDeleteMessagesConfirm(false)}
              onConfirm={handleDeleteMessages}
              title={t('groupInfoModal.modals.deleteMessagesTitle')}
              message={t('groupInfoModal.modals.deleteMessagesMessage', { name: group.name })}
            />

            <DeleteModal
              isOpen={showDeleteConfirm}
              onClose={() => setShowDeleteConfirm(false)}
              onConfirm={handleDeleteGroup}
              title={t('groupInfoModal.modals.deleteGroupTitle')}
              message={t('groupInfoModal.modals.deleteGroupMessage', { name: group.name })}
            />

            {showRemoveMemberConfirm && (
              <DeleteModal
                isOpen={!!showRemoveMemberConfirm}
                onClose={() => setShowRemoveMemberConfirm(null)}
                onConfirm={() => handleRemoveMember(showRemoveMemberConfirm.id)}
                title={t('groupInfoModal.removeMemberTitle')}
                message={t('groupInfoModal.modals.removeMemberMessage', { name: getDisplayName(showRemoveMemberConfirm) })}
              />
            )}

            {/* Add Member Modal */}
            <AnimatePresence>
              {showAddMemberModal && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => {
                      setShowAddMemberModal(false);
                      setAddMemberError(null);
                    }}
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100002]"
                  />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="fixed inset-0 flex items-center justify-center z-[100003] p-4 pointer-events-none"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col pointer-events-auto">
                      <div className="flex items-center justify-between p-4 border-b border-white/10 dark:border-white/10">
                        <h3 className="text-lg font-semibold text-[var(--color-text-primary)] dark:text-white">
                          {t('groupInfoModal.selectFriendTitle')}
                        </h3>
                        <button
                          onClick={() => {
                            setShowAddMemberModal(false);
                            setAddMemberError(null);
                          }}
                          className="p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="flex-1 overflow-y-auto p-4">
                        {(() => {
                          const availableFriends = friends?.filter(friend => {
                            if (friend.isDeleted) return false;
                            const friendId = friend.email || friend.id;
                            return !groupDetails?.members?.some(m => (m.email || m.id) === friendId);
                          }) || [];
                          
                          if (!friends || friends.length === 0) {
                            return (
                              <div className="text-center text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] py-8">
                                <p className="text-sm">{t('groupInfoModal.noFriendsYet')}</p>
                                <p className="text-xs mt-1">{t('groupInfoModal.addFriendFirst')}</p>
                              </div>
                            );
                          }

                          if (availableFriends.length === 0) {
                            return (
                              <div className="text-center text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] py-8">
                                <p className="text-sm">{t('groupInfoModal.allFriendsInGroup')}</p>
                              </div>
                            );
                          }

                          return (
                            <div className="space-y-2">
                              {availableFriends.map((friend) => {
                                const friendId = friend.email || friend.id;
                                return (
                                  <button
                                    key={friendId}
                                    onClick={() => handleAddMember(friend.email || friendId)}
                                    disabled={addingMember}
                                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-[var(--color-bg-muted)] dark:hover:bg-[#1e2d47] transition-colors text-left"
                                  >
                                    <div className="w-10 h-10 rounded-full bg-[#2563eb]/10 dark:bg-[#2563eb]/10 flex items-center justify-center text-sm font-semibold text-[#3d8ef0] dark:text-[#7ab8f8] flex-shrink-0">
                                      {friend.avatarUrl ? (
                                        <img
                                          src={friend.avatarUrl}
                                          alt={getDisplayName(friend)}
                                          className="w-full h-full rounded-full object-cover"
                                        />
                                      ) : (
                                        getInitials(getDisplayName(friend))
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-[var(--color-text-primary)] dark:text-white truncate">
                                        {getDisplayName(friend)}
                                      </p>
                                      {friend.email && (
                                        <p className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] truncate">
                                          {friend.email}
                                        </p>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>

                      {addMemberError && (
                        <div className="px-4 pb-4">
                          <p className="text-sm text-red-500 dark:text-red-400">{addMemberError}</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>

            {/* Member Context Menu (participant right-click) - Normal Modal Mode */}
            <ContextMenu
              isOpen={memberContextMenu.isOpen && !!memberContextMenu.member}
              position={{ x: memberContextMenu.x, y: memberContextMenu.y }}
              items={(() => {
                const member = memberContextMenu.member;
                if (!member) return [];

                const isCurrentUser = member.id === user?.id;
                const isFriend = friends?.some(
                  (f) => !f.isDeleted && (f.email || f.id) === member.email
                );

                const isLocked =
                  currentGroup?.isLocked === true || currentGroup?.is_locked === true;

                const canKickMember =
                  !isCurrentUser &&
                  !isLocked &&
                  member.role !== 'owner' &&
                  ((currentUserRole === 'owner') ||
                    (currentUserRole === 'admin' && member.role === 'member'));

                const canPromoteToAdmin =
                  currentUserRole === 'owner' &&
                  member.role === 'member' &&
                  !isCurrentUser;

                const canDemoteToMember =
                  currentUserRole === 'owner' &&
                  member.role === 'admin' &&
                  !isCurrentUser;

                return [
                  // Özel mesaj gönder
                  !isCurrentUser &&
                    isFriend && {
                      label: t('groupInfoModal.contextMenu.sendPrivateMessage'),
                      icon: MessageCircle,
                      onClick: () => {
                        const friendId = member.email || member.id;
                        if (friendId && selectFriend) {
                          selectFriend(friendId);
                        }
                      },
                    },
                  // Arkadaş ekle
                  !isCurrentUser &&
                    !isFriend &&
                    member.email && {
                      label: t('groupInfoModal.contextMenu.addFriend'),
                      icon: UserPlus,
                      onClick: async () => {
                        try {
                          await chatApi.sendFriendRequest({
                            token: authToken,
                            receiverEmail: member.email,
                          });
                          window.dispatchEvent(
                            new CustomEvent('notesplus-show-toast', {
                              detail: {
                                type: 'success',
                                title: t('groupInfoModal.contextMenu.friendRequestTitle'),
                                message: t('groupInfoModal.contextMenu.friendRequestMessage', { email: member.email }),
                              },
                            })
                          );
                        } catch (err) {
                          console.error(
                            'Send friend request error from member context menu (modal):',
                            err
                          );
                          const message = getApiErrorMessage(err, { fallback: t('groupInfoModal.contextMenu.friendRequestErrorFallback') });
                          window.dispatchEvent(
                            new CustomEvent('notesplus-show-toast', {
                              detail: {
                                type: 'error',
                                title: t('groupInfoModal.contextMenu.friendRequestErrorTitle'),
                                message,
                              },
                            })
                          );
                        }
                      },
                    },
                  // Yönetici yap (sadece Kurucu, hedef UYE)
                  canPromoteToAdmin && {
                    label: t('groupInfoModal.contextMenu.makeAdmin'),
                    icon: Shield,
                    onClick: async () => {
                      try {
                        await groupApi.promoteMember({
                          token: authToken,
                          groupId: currentGroup.id,
                          memberId: member.id,
                        });
                        await loadGroupDetails();
                      } catch (err) {
                        console.error('Promote member error (modal):', err);
                        const message = getApiErrorMessage(err, { fallback: t('groupInfoModal.contextMenu.makeAdminErrorFallback') });
                        window.dispatchEvent(
                          new CustomEvent('notesplus-show-toast', {
                            detail: {
                              type: 'error',
                              title: t('common:errorTitle'),
                              message,
                            },
                          })
                        );
                      }
                    },
                  },
                  // Üye yap (sadece Kurucu, hedef YONETICI)
                  canDemoteToMember && {
                    label: t('groupInfoModal.contextMenu.makeMember'),
                    icon: Users,
                    onClick: async () => {
                      try {
                        await groupApi.demoteMember({
                          token: authToken,
                          groupId: currentGroup.id,
                          memberId: member.id,
                        });
                        await loadGroupDetails();
                      } catch (err) {
                        console.error('Demote member error (modal):', err);
                        const message = getApiErrorMessage(err, { fallback: t('groupInfoModal.contextMenu.makeMemberErrorFallback') });
                        window.dispatchEvent(
                          new CustomEvent('notesplus-show-toast', {
                            detail: {
                              type: 'error',
                              title: t('common:errorTitle'),
                              message,
                            },
                          })
                        );
                      }
                    },
                  },
                  // Gruptan at
                  canKickMember && {
                    label: t('groupInfoModal.contextMenu.kick'),
                    icon: UserMinus,
                    danger: true,
                    onClick: () => {
                      setShowRemoveMemberConfirm(member);
                    },
                  },
                ];
              })()}
              onClose={() =>
                setMemberContextMenu({ isOpen: false, x: 0, y: 0, member: null })
              }
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>

    <AvatarCropModal
      isOpen={showGroupCropModal}
      onClose={() => setShowGroupCropModal(false)}
      imageFile={selectedGroupImageFile}
      onSave={handleGroupAvatarSave}
      title={t('groupInfoModal.avatarCropTitle')}
    />
    </>
  );
};

export default GroupInfoModal;
